import React from 'react';
import { ArrowsClockwise, MagnifyingGlass, X, CaretLeft, CaretRight, CheckCircle, WarningCircle, Minus, Envelope } from '@phosphor-icons/react';

const CARD = 'rounded-card border border-paper-border bg-paper-panel shadow-soft';
const INPUT = 'rounded-field border border-paper-border2 bg-paper px-3 py-2 text-[13px] text-ink outline-none focus:border-ink';
const LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-ink-faint';

const STATUS_CFG = {
  responded: { cls: 'bg-status-resolved/15 text-status-resolved',            icon: CheckCircle,   label: 'Responded' },
  failed:    { cls: 'bg-status-escalated/15 text-status-escalated',          icon: WarningCircle, label: 'Failed'    },
  no_action: { cls: 'bg-paper-muted text-ink-muted',                         icon: Minus,         label: 'No action' },
};
function StatusChip({ status }) {
  const cfg = STATUS_CFG[status] || { cls: 'bg-paper-muted text-ink-faint', label: status || '—', icon: Minus };
  const I = cfg.icon;
  return <span className={`inline-flex items-center gap-1 rounded-chip px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`}><I size={11} weight="fill" />{cfg.label}</span>;
}

function CategoryChip({ cat }) {
  if (!cat) return <span className="text-[11px] text-ink-faint">—</span>;
  return <span className="inline-flex rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-medium text-ink-muted">{cat}</span>;
}

// KPI card
function Kpi({ label, value, sublabel, tone }) {
  const tones = {
    total:     'text-ink',
    responded: 'text-status-resolved',
    failed:    'text-status-escalated',
    rate:      'text-status-under-process',
  };
  const cls = tones[tone] || 'text-ink';
  return (
    <div className={`${CARD} p-4`}>
      <div className={LABEL}>{label}</div>
      <div className={`mt-1 font-serif text-3xl ${cls}`}>{value == null ? '—' : value}{tone === 'rate' && value != null ? '%' : ''}</div>
      {sublabel && <div className="mt-0.5 text-[11px] text-ink-faint">{sublabel}</div>}
    </div>
  );
}

// Horizontal bar list for category / status breakdowns
function BarList({ rows, valueKey = 'n', labelKey, color = 'bg-status-under-process/70' }) {
  const max = rows.reduce((m, r) => Math.max(m, r[valueKey] || 0), 0);
  if (!rows.length) return <div className="py-6 text-center text-[13px] text-ink-faint">No data</div>;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const label = r[labelKey];
        const val = r[valueKey] || 0;
        const pct = max ? Math.max(2, Math.round((val / max) * 100)) : 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-28 truncate text-[12px] text-ink-muted">{label || '—'}</div>
            <div className="flex-1 h-3.5 rounded bg-paper-muted overflow-hidden">
              <div className={`h-full ${color} rounded`} style={{ width: pct + '%' }} />
            </div>
            <div className="w-12 text-right text-[12px] font-medium text-ink">{val}</div>
          </div>
        );
      })}
    </div>
  );
}

// Volume-over-time inline SVG bar chart
function VolumeChart({ volume }) {
  if (!volume || !volume.length) return <div className="py-8 text-center text-[13px] text-ink-faint">No volume data</div>;
  const W = 720, H = 160, PAD = 24;
  const days = volume.map(v => ({ day: v.day, n: v.n }));
  const max = Math.max(1, ...days.map(d => d.n));
  const barW = Math.max(4, Math.floor((W - PAD * 2) / days.length) - 2);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: '480px', height: '160px' }}>
        {/* baseline */}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="currentColor" className="text-paper-border2" strokeWidth="1" />
        {days.map((d, i) => {
          const x = PAD + i * (barW + 2);
          const h = Math.round(((d.n / max) * (H - PAD * 2)));
          const y = H - PAD - h;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={h} className="fill-status-under-process/70" rx="1">
                <title>{d.day + ': ' + d.n}</title>
              </rect>
            </g>
          );
        })}
        {/* first + last day labels */}
        {days.length > 0 && <text x={PAD} y={H - 6} className="fill-current text-ink-faint text-[10px]">{days[0].day}</text>}
        {days.length > 1 && <text x={W - PAD} y={H - 6} textAnchor="end" className="fill-current text-ink-faint text-[10px]">{days[days.length - 1].day}</text>}
      </svg>
    </div>
  );
}

// Sortable column header
function Th({ label, col, sortBy, sortOrder, onSort }) {
  const active = sortBy === col;
  return (
    <button type="button" onClick={() => onSort(col)}
      className={`flex items-center gap-1 text-left ${active ? 'text-ink' : 'text-ink-faint'}`}>
      {label}
      <span className="text-[9px]">{active ? (sortOrder === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { const d = new Date(iso); return d.toLocaleString(); } catch { return iso; }
}

// Detail drawer
function DetailDrawer({ row, onClose, onOpenTicket }) {
  if (!row) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col overflow-hidden border-l border-paper-border bg-paper-panel shadow-soft">
        <div className="flex items-start justify-between border-b border-paper-border p-5">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <StatusChip status={row.status} />
              <CategoryChip cat={row.category} />
              {row.ticket_number && (
                <button onClick={() => onOpenTicket && onOpenTicket(row.ticket_number.replace(/^\D+/, ''))}
                  className="rounded-chip bg-status-under-process/10 px-2 py-0.5 text-[11px] font-medium text-status-under-process hover:underline">
                  → {row.ticket_number}
                </button>
              )}
            </div>
            <h2 className="font-serif text-lg text-ink truncate" dir="auto">{row.subject || '(no subject)'}</h2>
            <p className="mt-0.5 text-[12px] text-ink-muted">{row.sender_name || '—'} · {row.sender_email || '—'} · {fmtDate(row.received_at)}</p>
          </div>
          <button onClick={onClose} className="ml-3 inline-flex h-8 w-8 flex-none items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <section>
            <div className={LABEL}>Original email body</div>
            <div className="mt-2 whitespace-pre-wrap rounded-field border border-paper-border2 bg-paper p-3 text-[13px] text-ink" dir="auto">
              {row.body_text || <span className="italic text-ink-faint">Not captured</span>}
            </div>
          </section>
          <section>
            <div className={LABEL}>Response sent</div>
            <div className="mt-2 whitespace-pre-wrap rounded-field border border-paper-border2 bg-paper p-3 text-[13px] text-ink" dir="auto">
              {row.response_text || <span className="italic text-ink-faint">{row.status === 'no_action' ? 'No reply was sent (noise / filtered).' : row.status === 'failed' ? 'Reply failed. See execution log.' : 'Not captured'}</span>}
            </div>
          </section>
          {(row.hubspot_ticket_id || row.execution_id || row.entity) && (
            <section>
              <div className={LABEL}>Metadata</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-ink-muted">
                {row.entity && <div><span className="text-ink-faint">Entity:</span> {row.entity}</div>}
                {row.hubspot_ticket_id && <div><span className="text-ink-faint">HubSpot ID:</span> {row.hubspot_ticket_id}</div>}
                {row.execution_id && <div className="col-span-2"><span className="text-ink-faint">Execution:</span> {row.execution_id}</div>}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

const COLS = 'grid grid-cols-[150px_120px_1fr_1fr_130px_110px_100px] gap-3 items-center';

export default function EmailMonitorView({
  rows, total, stats, entities, loading,
  filters, onFilterChange, onClearFilters,
  sortBy, sortOrder, onSort,
  offset, pageSize, onPage, onRowClick, onRefresh,
  selected, onCloseDetail, onOpenTicket, currentUser,
}) {
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Distinct-list options for filters — derived from current rows + stats
  const cats = [...new Set([...(stats?.by_category || []).map(c => c.category), ...rows.map(r => r.category).filter(Boolean)])].filter(c => c && c !== '(uncategorized)');

  return (
    <div className="p-1">
      <div className="space-y-5">

        {/* Header + refresh */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl text-ink flex items-center gap-2">
              <Envelope size={22} weight="fill" className="text-status-under-process" />
              Email Monitor
            </h1>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              Every email the CS agent has processed. {stats?.total != null ? `${stats.total} in scope.` : ''}
            </p>
          </div>
          <button onClick={onRefresh} title="Refresh"
            className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
            <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi label="Total received" value={stats?.total ?? 0} tone="total" />
          <Kpi label="Responded" value={stats?.responded ?? 0} tone="responded" />
          <Kpi label="Failed" value={stats?.failed ?? 0} tone="failed" />
          <Kpi label="Response rate" value={stats?.response_rate_pct} tone="rate" sublabel="responded / total" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className={`${CARD} p-4`}>
            <div className={`${LABEL} mb-3`}>By category</div>
            <BarList rows={(stats?.by_category || []).slice(0, 8)} labelKey="category" />
          </div>
          <div className={`${CARD} p-4`}>
            <div className={`${LABEL} mb-3`}>By status</div>
            <BarList rows={(stats?.by_status || [])} labelKey="status" color="bg-status-resolved/70" />
          </div>
          <div className={`${CARD} p-4`}>
            <div className={`${LABEL} mb-3`}>Volume (per day)</div>
            <VolumeChart volume={stats?.volume || []} />
          </div>
        </div>

        {/* Per-entity split (super_admin only) */}
        {isSuperAdmin && (stats?.by_entity || []).length > 1 && (
          <div className={`${CARD} p-4`}>
            <div className={`${LABEL} mb-3`}>By entity</div>
            <BarList rows={stats.by_entity} labelKey="entity" color="bg-entity-abraaj/70" />
          </div>
        )}

        {/* Filters + table */}
        <div className={CARD}>
          <div className="flex flex-wrap items-center gap-2 border-b border-paper-border p-4">
            <div className="flex items-center gap-2 rounded-field border border-paper-border2 bg-paper px-3 py-2 text-ink-faint">
              <MagnifyingGlass size={15} />
              <input type="text" placeholder="Search sender / subject / body"
                value={filters.search}
                onChange={(e) => onFilterChange('search', e.target.value)}
                className="w-64 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-faint" />
            </div>
            {isSuperAdmin && (
              <select value={filters.entity} onChange={(e) => onFilterChange('entity', e.target.value)} className={INPUT}>
                <option value="">All entities</option>
                {entities.map(e => <option key={e.id} value={e.slug}>{e.name}</option>)}
              </select>
            )}
            <select value={filters.category} onChange={(e) => onFilterChange('category', e.target.value)} className={INPUT}>
              <option value="">All categories</option>
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => onFilterChange('status', e.target.value)} className={INPUT}>
              <option value="">All statuses</option>
              <option value="responded">Responded</option>
              <option value="failed">Failed</option>
              <option value="no_action">No action</option>
            </select>
            <input type="date" value={filters.date_from} onChange={(e) => onFilterChange('date_from', e.target.value)}
              className={INPUT} title="From" />
            <input type="date" value={filters.date_to} onChange={(e) => onFilterChange('date_to', e.target.value)}
              className={INPUT} title="To" />
            <button onClick={onClearFilters}
              className="ml-auto rounded-field border border-paper-border2 px-3 py-2 text-[13px] text-ink-muted hover:bg-paper-muted">
              Clear filters
            </button>
          </div>

          {/* header row */}
          <div className={`${COLS} border-b border-paper-border px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-ink-faint`}>
            <Th label="Received" col="received_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <Th label="Entity" col="entity" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <Th label="Sender" col="sender_email" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <Th label="Subject" col="subject" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <Th label="Category" col="category" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <Th label="Status" col="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <span>Ticket</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-ink-faint">
              <ArrowsClockwise size={26} className="animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center">
              <p className="font-serif text-lg text-ink">No emails match your filters</p>
              <p className="mt-1 text-[13px] text-ink-muted">Try clearing filters or widening the date range.</p>
            </div>
          ) : (
            rows.map((r, i) => (
              <div key={r.id}
                onClick={() => onRowClick && onRowClick(r)}
                className={`${COLS} cursor-pointer px-5 py-3 hover:bg-paper-muted/50 ${i < rows.length - 1 ? 'border-b border-paper-border' : ''}`}>
                <span className="text-[12px] text-ink-muted">{fmtDate(r.received_at)}</span>
                <span className="text-[12px] text-ink-muted">{r.entity || '—'}</span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] text-ink" dir="auto">{r.sender_name || r.sender_email || '—'}</span>
                  {r.sender_name && r.sender_email && <span className="block truncate text-[11px] text-ink-faint">{r.sender_email}</span>}
                </span>
                <span className="truncate text-[13px] text-ink" dir="auto">{r.subject || '(no subject)'}</span>
                <CategoryChip cat={r.category} />
                <StatusChip status={r.status} />
                <span className="text-[12px] text-ink-muted">{r.ticket_number || <span className="text-ink-faint">—</span>}</span>
              </div>
            ))
          )}

          {!loading && total > pageSize && (
            <div className="flex items-center justify-between border-t border-paper-border px-5 py-4">
              <span className="text-[13px] text-ink-muted">{offset + 1}–{offset + rows.length} of {total}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => onPage('prev')} disabled={offset === 0}
                  className="inline-flex items-center gap-1.5 rounded-field border border-paper-border2 px-3 py-1.5 text-[13px] text-ink-muted disabled:opacity-40">
                  <CaretLeft size={13} />Prev
                </button>
                <button onClick={() => onPage('next')} disabled={offset + pageSize >= total}
                  className="inline-flex items-center gap-1.5 rounded-field border border-paper-border2 px-3 py-1.5 text-[13px] text-ink disabled:opacity-40">
                  Next<CaretRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DetailDrawer row={selected} onClose={onCloseDetail} onOpenTicket={onOpenTicket} />
    </div>
  );
}
